import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

export type WizardPairKind = 'pair' | 'new-to-aivor' | 'missing-aivor';
export type WizardPairChoice = 'same' | 'different' | 'add' | 'leave-out' | 'picked';

export interface WizardRecordOption {
  id: number;
  name: string;
  meta: string;
}

interface ChoiceOption {
  key: WizardPairChoice;
  title: string;
  text: string;
}

@Component({
  selector: 'app-wizard-pair-card',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './wizard-pair-card.component.html',
  styleUrls: ['./sage-wizard.shared.css', './wizard-pair-card.component.css'],
})
export class WizardPairCardComponent {
  @Input() kind: WizardPairKind = 'pair';
  @Input() matchId = 0;
  @Input() noun = 'record';
  @Input() sageName = '';
  @Input() sageMeta = '';
  @Input() aivorId: number | null = null;
  @Input() aivorName: string | null = null;
  @Input() aivorMeta = '';
  /** Why the matcher held this record, for example a possible duplicate. */
  @Input() reason: string | null = null;
  @Input() choice: WizardPairChoice | null = null;
  @Input() answered = false;
  @Input() saving = false;
  @Input() records: WizardRecordOption[] = [];
  /** Aivor record id → the Sage row that already holds it, so it can't be picked twice. */
  @Input() claims: ReadonlyMap<number, { matchId: number; name: string }> = new Map();

  @Output() choose = new EventEmitter<WizardPairChoice>();
  @Output() pick = new EventEmitter<number>();

  readonly candidateLimit = 8;
  pickerOpen = false;
  query = '';

  get title(): string {
    switch (this.kind) {
      case 'new-to-aivor':
        return `Is this a new ${this.noun}?`;
      case 'missing-aivor':
        return `The Aivor ${this.noun} this was matched to is gone`;
      default:
        return `Are these the same ${this.noun}?`;
    }
  }

  get subtitle(): string | null {
    switch (this.kind) {
      case 'new-to-aivor':
        return `We could not find this ${this.noun} in Aivor. It may be there under another name.`;
      case 'missing-aivor':
        return 'It may have been deleted. Add the Sage one to Aivor, or pick another Aivor record.';
      default:
        return null;
    }
  }

  get options(): ChoiceOption[] {
    switch (this.kind) {
      case 'new-to-aivor':
        return [
          { key: 'add', title: 'Yes, add it to Aivor', text: `Add the Sage ${this.noun} to Aivor as new.` },
          { key: 'leave-out', title: 'No, leave it out', text: 'It stays in Sage only.' },
        ];
      case 'missing-aivor':
        return [{ key: 'add', title: 'Add it to Aivor as new', text: `Bring the Sage ${this.noun} into Aivor.` }];
      default:
        return [
          { key: 'same', title: `Yes, same ${this.noun}`, text: 'Link them. Aivor keeps its name.' },
          { key: 'different', title: 'No, different', text: 'Add the Sage one to Aivor as new.' },
        ];
    }
  }

  get pickLabel(): string {
    return this.kind === 'pair' ? `Pick a different Aivor ${this.noun}` : `It is already in Aivor: pick the ${this.noun}`;
  }

  get missingLabel(): string {
    return this.kind === 'missing-aivor' ? 'No longer in Aivor' : 'Not found in Aivor';
  }

  private get matching(): WizardRecordOption[] {
    const q = this.query.trim().toLowerCase();
    return this.records.filter(
      (r) => r.id !== this.aivorId && (!q || r.name.toLowerCase().includes(q) || r.meta.toLowerCase().includes(q))
    );
  }

  get candidates(): WizardRecordOption[] {
    return this.matching.slice(0, this.candidateLimit);
  }

  get candidateTotal(): number {
    return this.matching.length;
  }

  claimedBy(record: WizardRecordOption): string | null {
    const claim = this.claims.get(record.id);
    return claim && claim.matchId !== this.matchId ? claim.name : null;
  }

  togglePicker(): void {
    this.pickerOpen = !this.pickerOpen;
    this.query = '';
  }

  pickRecord(record: WizardRecordOption): void {
    if (this.saving || this.claimedBy(record)) return;
    this.pick.emit(record.id);
    this.pickerOpen = false;
  }
}
